import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useStyles from 'isomorphic-style-loader/useStyles';

import { IconReactionPositiveSmall } from '@sololearnorg/sol';
import { AuthService, Container, i18n } from '../../../../symphony';

import { orderFilerTypeAlias, SlCodeBitsContext } from '../../global-constants';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { ActionType, RequestType } from '../../global-interface';
import { SlDataService } from '../../services/sl-data.service';
import { Meta } from '../Meta/Meta';
import { NewCode } from '../NewCode/NewCode';
import { CodeBitsListLoading } from './CodeBitsListLoading';
import { EmptyCodeBits } from '../EmptyCodeBits/EmptyCodeBits';
import { useComponentWillMount } from '../../../../shared/public/SlUtils/useComponentWillMount';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { LocalizedLink } from '../../../../shared/public/LocalizedLink/LocalizedLink';

import s from './CodeBitsList.scss';

export function CodeBitsList() {
	useStyles(s);
	const isLoggedIn = Container.take('global', AuthService).isLoggedIn();
	const { actions$ } = Container.take(SlCodeBitsContext, SlActions);
	const [searchParams] = useSearchParams();
	const currentPageNumber = +searchParams.get('page') || 1;
	const codeBitsListSearchText = searchParams.get('query') || '';
	const orderFilter = searchParams.get('ordering') || Object.values(orderFilerTypeAlias)[0];
	const languageFilter = searchParams.get('language') || 'all';

	const isMyCodeBits = orderFilter === 'MyCodeBits';

	useComponentWillMount(() => {
		if (!(isMyCodeBits && !isLoggedIn)) {
			actions$.next({
				type: ActionType.OnCodeBitsListMount,
				requestType: RequestType.Parallel,
				data: {
					orderFilter,
					languageFilter,
					codeBitsListSearchText,
					currentPageNumber,
				},
			});
		}
	});

	const {
		codeBitsList$: codeBitsList,
		isCodeBitsListLoading$: isCodeBitsListLoading,
		supportedLanguages$: supportedLanguages,
	} = useContainerData(SlCodeBitsContext, SlDataService, [
		'codeBitsList$',
		'isCodeBitsListLoading$',
		'supportedLanguages$',
	]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [currentPageNumber]);

	if (isCodeBitsListLoading) {
		return <CodeBitsListLoading />;
	}

	if (!codeBitsList?.length || (isMyCodeBits && !isLoggedIn)) {
		return (
			<EmptyCodeBits
				isMyCodeBits={isMyCodeBits}
				isSearched={!!codeBitsListSearchText}
			/>
		);
	}

	const onProjectClick = (codeId: string) => {
		actions$.next({
			type: ActionType.OnCodeBitClick,
			requestType: RequestType.Parallel,
			data: {
				codeId,
			},
		});
	};

	return (
		<div className="sl-code-bits-list">
			{codeBitsList.map((project) => (
				<div className="sl-code-bits-list__item" key={project.id}>
					<LocalizedLink
						target="_blank"
						to={`/compiler-playground/${project.publicId}`}
						onClick={() => onProjectClick(project.publicId)}
					>
						<h5
							className="sl-code-bits-list__item__icon"
							style={{
								backgroundColor: supportedLanguages.find(
									(l) => project.language === l.key,
								)?.color,
							}}
						>
							{project.language}
						</h5>
					</LocalizedLink>
					<div className="sl-code-bits-list__item__right-part">
						<LocalizedLink
							target="_blank"
							to={`/compiler-playground/${project.publicId}`}
							className="sl-code-bits-list__item__title"
							onClick={() => onProjectClick(project.publicId)}
						>
							{project.name}
						</LocalizedLink>
						<div className="sl-code-bits-list__item__bottom-part">
							<div className="sl-code-bits-list__item__info">
								<div className="sl-code-bits-list__item__info__votes">
									<IconReactionPositiveSmall />
									{' '}
									{i18n.t('web-code.code-votes-count', {
										count: project.votes,
									})}
								</div>
							</div>
							<Meta
								date={project.createdDate}
								avatarUrl={project.avatarUrl}
								userName={project.userName}
								userID={project.userId}
								badge={project.badge}
							/>
						</div>
					</div>
				</div>
			))}
			<NewCode />
		</div>
	);
}
